import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import { useNavigate } from "react-router-dom";

// Import components and pages
import LoginPage from "./pages/LoginPage";
import { Home } from "./pages/Home";
import KarigarOrdersStas from "./pages/KarigarOrdersStats";
import { OrderProvider } from "./contexts/OrderContext";
import { Footer } from "./pages/Footer";
import Header from "./pages/Header";
import NewOrderPage from "./pages/Status/NewOrderPage";
import Delivered from "./pages/Status/Delivered";
import Completed from "./pages/Status/Completed";
import Issued from "./pages/Status/Issued";
import Rejected from "./pages/Status/Rejected";
import RejectedByMFr from "./pages/Status/RejectedByMfr";
import Inprocess from "./pages/Status/Inprocess";
import Pending from "./pages/Status/Pending";
import Overdue from "./pages/Status/OverdueOrders";
import TodayDelivery from "./pages/Status/TodayDeliveryOrders";
import Ordercreation from "./pages/Ordercreation";
import CustomerOrderCreate from "./pages/customer/CustomerOrderCreate";
import MyOrders from "./pages/customer/MyOrders";
import CustomerHome from "./pages/customer/CustomerHome";
import OrderDetails from "./pages/Status/OrderDetails";
import OrderList from "./pages/OrderSearch";
import CompleteOrder from "./pages/Status/CompleteOrder";
import QRScanner from "./pages/QRScanner";
import StockScanner from "./pages/QRFetchOnly";
import PartialComplete from "./pages/Status/Partial";
import DeliveryWorkShop from "./pages/Status/DeliveryWorkShop";
import FilterForm from "./pages/filter/FilterForm";
import StockDataPage from "./pages/filter/StockDataPage";
import CreateUser from "./pages/master/CreateUser";
import FilterInfo from "./pages/filter/FilterInfo";
import CreateMetalType from "./pages/master/CreateMetalType";
import CreateItemType from "./pages/master/CreateItemType";
import CreateDesign from "./pages/master/CreateDesign";
import CreateStoneMaster from "./pages/master/CreateStoneMaster";
import useFocusHandler from "./focusKeyboard";
import PeopleList from "./pages/master/PeopleList";
import BulkOrderCreation from "./pages/BulkOrderCreation";
import CompleteOrderWithoutQR from "./pages/Status/CompleteOrderWithoutQR";
import DesignCatalogue from "./pages/DesignCatalogue";
import EditDesignCatalogue from "./pages/EditDesignCatalogue";
import ExcelOrdersCreation from "./pages/ExcelOrdersCreation";
import ExhibitionOrdersCreation from "./pages/ExhibitionOrdersCreation";
import ProtectedStockDataRoute from "./upgraded-filter/ProtectedStockDataRoute";
import CreateFilterLink from "./upgraded-filter/CreateFilterLink";
import AdminDashboard from "./upgraded-filter/AdminDashboard";
import ShowActiveLinks from "./upgraded-filter/ShowActiveLinks";
import PendingReq from "./upgraded-filter/PendingReq";
import UsersGroupMaster from "./upgraded-filter/UsersGroupMaster";
import NewStockDataPage from "./upgraded-filter/NewStockDataPage";
import ActiveUser from "./upgraded-filter/ActiveUsers";
import UserActivityLogs from "./upgraded-filter/UserActivityLog";
import ShowActiveLinkLogs from "./upgraded-filter/ShowActiveLinkLogs";
import StoreLogs from "./upgraded-filter/StoreLogs";
import OrderSummaryTable from "./upgraded-filter/OrderSummaryTable";
import OrderDetailsTable from "./upgraded-filter/OrderDetailsTable";
import SetCard from "./upgraded-filter/SetCard";
import WarehouseDashboard from "./upgraded-filter/WarehouseDashboard";
import GuestProductList from "./upgraded-filter/GuestProductList";
import SalesDashboard from "./upgraded-filter/SalesDashboard";
import OrderStatusTable from "./upgraded-filter/OrderStatusTable";
import NewUserRequests from "./upgraded-filter/NewUserRequests";
import AllUsers from "./upgraded-filter/AllUsers";

// ProtectedRoute component
const ProtectedRoute = ({ element: Component }) => {
  const user = localStorage.getItem("authToken");
  return user ? Component : <Navigate to="/login" />;
};

// Customers get their own dashboard
const HomeByRole = () =>
  localStorage.getItem("userRole") === "Customer" ? <CustomerHome /> : <Home />;

// Customers get the simplified self-order screen
const OrderCreateByRole = () =>
  localStorage.getItem("userRole") === "Customer" ? (
    <CustomerOrderCreate />
  ) : (
    <Ordercreation />
  );

// Blocks screens customers shouldn't reach (QR scanning, the manufacturer's
// status pages with status-change controls)
const NoCustomer = ({ element: Component, redirectTo = "/home" }) => {
  return localStorage.getItem("userRole") === "Customer" ? (
    <Navigate to={redirectTo} replace />
  ) : (
    Component
  );
};

function AppWithFooter() {
  const navigate = useNavigate();
  useFocusHandler();

  const username = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "BACK_BUTTON_PRESSED") {
        console.log("Back button pressed in iframe");

        // React Router v6 does not expose full history, but we can use navigate(-1)
        if (window.history.length > 1) {
          navigate(-1); // Go back
        } else {
          console.log(
            "No more history to go back to, allowing default behavior",
          );
          window.parent.postMessage(
            { type: "ALLOW_DEFAULT_BACK_BEHAVIOR" },
            "*",
          );
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [navigate]);

  const { pathname } = useLocation();

  // Exclude header/footer for these routes
  const excludeHeaderFooter = ["/", "/login"];
  const excludeFooterOnly = ["/completeorder"];
  const isStockDataPage = pathname.startsWith("/getstockdata");
  const isLinkLog = pathname.startsWith("/active-link-logs");
  const isDMOrderSummary = pathname.startsWith("/order-summary");
  const isOrderStatusScreen = pathname.startsWith("/order-status");

  // Check if it's a dynamic login code (e.g., "/xyz")
  const isDynamicCode =
    !pathname.startsWith("/status") &&
    !pathname.startsWith("/order") &&
    !pathname.startsWith("/my-orders") &&
    !pathname.startsWith("/bulkorder") &&
    !pathname.startsWith("/bulkexhibitionorder") &&
    !pathname.startsWith("/filter") &&
    !pathname.startsWith("/create") &&
    !pathname.startsWith("/qrscan") &&
    !pathname.startsWith("/stockscan") &&
    !pathname.startsWith("/home") &&
    !pathname.startsWith("/userlist") &&
    !pathname.startsWith("/getstockdata") &&
    !pathname.startsWith("/createdesigncatalogue") &&
    !pathname.startsWith("/editcatalogue") &&
    pathname.split("/").length === 2;

  const showHeader =
    !excludeHeaderFooter.includes(pathname) &&
    !isStockDataPage &&
    !isLinkLog &&
    !isDMOrderSummary &&
    !isDynamicCode &&
    !isOrderStatusScreen;
  const showFooter =
    !isLinkLog &&
    !excludeHeaderFooter.includes(pathname) &&
    !isStockDataPage &&
    !excludeFooterOnly.includes(pathname) &&
    !isDMOrderSummary &&
    !isDynamicCode &&
    !isOrderStatusScreen &&
    username.role !== "QC" &&
    username.role !== "DIGITAL ORDER" &&
    username.role !== "DIGITAL MARKETER";
  return (
    <div className="App">
      {showHeader && <Header />}
      <Routes>
        {/* Login Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<LoginPage />} />

        {/* Encrypted Stock Data Route (No Header/Footer) */}
        {/* For preservation of older links */}
        <Route
          path="/getstockdata/:linkName/:linkId/:mfr_id/:encryptedType"
          element={<ProtectedStockDataRoute />}
        />
        <Route
          path="/getstockdata/set/:linkName/:linkId/:mfr_id/:encryptedType"
          element={<ProtectedStockDataRoute />}
        />
        {/* =================== */}

        <Route
          path="/getstockdata/:linkName/:store_id/:linkId/:mfr_id/:encryptedType/:contactDM"
          element={<ProtectedStockDataRoute />}
        />
        <Route
          path="/getstockdata/set/:linkName/:store_id/:linkId/:mfr_id/:encryptedType/:contactDM"
          element={<ProtectedStockDataRoute />}
        />

        <Route path="/getstockdata/:filterId" element={<StockDataPage />} />

        {/* Protected Routes */}
        <Route path="/home" element={<ProtectedRoute element={<HomeByRole />} />} />
        <Route
          path="/status/karigarstat"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<KarigarOrdersStas />} />} />}
        />
        <Route
          path="/status/new"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<NewOrderPage />} />} />}
        />
        <Route
          path="/status/delivered"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<Delivered />} />} />}
        />
        <Route
          path="/status/ready"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<Completed />} />} />}
        />
        <Route
          path="/status/Issued"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<Issued />} />} />}
        />
        <Route
          path="/status/rejected"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<Rejected />} />} />}
        />
        <Route
          path="/status/rejectedbymfr"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<RejectedByMFr />} />} />}
        />
        <Route
          path="/status/inProcess"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<Inprocess />} />} />}
        />
        <Route
          path="/status/pending"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<Pending />} />} />}
        />
        <Route
          path="/status/overdue"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<Overdue />} />} />}
        />
        <Route
          path="/status/todayDelivery"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<TodayDelivery />} />} />}
        />
        <Route
          path="/order"
          element={<ProtectedRoute element={<OrderCreateByRole />} />}
        />
        <Route
          path="/my-orders"
          element={<ProtectedRoute element={<MyOrders />} />}
        />
        {/* <Route
          path="/bulkorder"
          element={<ProtectedRoute element={<BulkOrderCreation />} />}
        /> */}
        <Route
          path="/bulkorder"
          element={<ProtectedRoute element={<ExcelOrdersCreation />} />}
        />
        <Route
          path="/bulkexhibitionorder"
          element={<ProtectedRoute element={<ExhibitionOrdersCreation />} />}
        />
        <Route
          path="/order-details"
          element={<ProtectedRoute element={<OrderDetails />} />}
        />
        <Route
          path="/order-list"
          element={<ProtectedRoute element={<OrderList />} />}
        />
        <Route
          path="/filter"
          element={<ProtectedRoute element={<FilterForm />} />}
        />
        <Route
          path="/userlist"
          element={<ProtectedRoute element={<PeopleList />} />}
        />
        <Route
          path="/createuser"
          element={<ProtectedRoute element={<CreateUser />} />}
        />
        <Route
          path="/createmetal"
          element={<ProtectedRoute element={<CreateMetalType />} />}
        />
        <Route
          path="/createitem"
          element={<ProtectedRoute element={<CreateItemType />} />}
        />
        <Route
          path="/createdesign"
          element={<ProtectedRoute element={<CreateDesign />} />}
        />
        <Route
          path="/createstone"
          element={<ProtectedRoute element={<CreateStoneMaster />} />}
        />
        <Route
          path="/completeorder"
          element={<ProtectedRoute element={<CompleteOrder />} />}
        />
        <Route
          path="/completeorderwithoutqr"
          element={<ProtectedRoute element={<CompleteOrderWithoutQR />} />}
        />
        <Route
          path="/qrscan"
          element={<ProtectedRoute element={<NoCustomer element={<QRScanner />} />} />}
        />
        <Route
          path="/stockscan"
          element={<ProtectedRoute element={<NoCustomer element={<StockScanner />} />} />}
        />
        <Route
          path="/status/partial"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<PartialComplete />} />} />}
        />
        <Route
          path="/status/delivertoworkshop"
          element={<ProtectedRoute element={<NoCustomer redirectTo="/my-orders" element={<DeliveryWorkShop />} />} />}
        />
        <Route
          path="/filterinfo/:filterId"
          element={<ProtectedRoute element={<FilterInfo />} />}
        />
        <Route
          path="/createdesigncatalogue"
          element={<ProtectedRoute element={<DesignCatalogue />} />}
        />
        <Route
          path="/editcatalogue"
          element={<ProtectedRoute element={<EditDesignCatalogue />} />}
        />
        <Route
          path="/admin-dash"
          element={<ProtectedRoute element={<AdminDashboard />} />}
        />

        <Route
          path="/show-active-links"
          element={<ProtectedRoute element={<ShowActiveLinks />} />}
        />

        <Route
          path="/uc_createfilter"
          element={<ProtectedRoute element={<CreateFilterLink />} />}
        />

        <Route
          path="/pending-requests"
          element={<ProtectedRoute element={<PendingReq />} />}
        />

        <Route
          path="/users-group"
          element={<ProtectedRoute element={<UsersGroupMaster />} />}
        />
        <Route
          path="/active-users"
          element={<ProtectedRoute element={<ActiveUser />} />}
        />
        <Route
          path="/user-activity-log"
          element={<ProtectedRoute element={<UserActivityLogs />} />}
        />
        <Route
          path="/active-link-logs/:linkId"
          element={<ProtectedRoute element={<ShowActiveLinkLogs />} />}
        />

        <Route
          path="/order-summary"
          element={<ProtectedRoute element={<OrderSummaryTable />} />}
        />
        <Route
          path="/order-summary-details"
          element={<ProtectedRoute element={<OrderDetailsTable />} />}
        />
        <Route
          path="/warehouse-dashboard"
          element={<ProtectedRoute element={<WarehouseDashboard />} />}
        />
        <Route
          path="/sales-dashboard"
          element={<ProtectedRoute element={<SalesDashboard />} />}
        />
        <Route
          path="/guest"
          element={<ProtectedRoute element={<GuestProductList />} />}
        />

        <Route
          path="/order-status"
          element={<ProtectedRoute element={<OrderStatusTable />} />}
        />

        <Route
          path="/new-user-requests"
          element={<ProtectedRoute element={<NewUserRequests />} />}
        />

        <Route
          path="/all-users"
          element={<ProtectedRoute element={<AllUsers />} />}
        />

        {/* Dynamic Code (Login via Code) */}
        {isDynamicCode && <Route path="/:code" element={<LoginPage />} />}
      </Routes>

      {showFooter && <Footer />}
    </div>
  );
}

function App() {
  return (
    <OrderProvider>
      <Router>
        <AppWithFooter />
      </Router>
    </OrderProvider>
  );
}

export default App;
